import InfoIcon from "@assets/info.svg";
import { css } from "@emotion/react";
import { Autocomplete, createFilterOptions } from "@mui/material";
import { useTreeViewApiRef } from "@mui/x-tree-view/hooks";
import { RichTreeView } from "@mui/x-tree-view/RichTreeView";
import type { SyntheticEvent } from "react";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { trpc } from "../../utils/trpc";
import { LoadingSpinner } from "../bits/loading";
import {
  SearchInput,
  SearchInputContainer,
  SearchInputLabel,
} from "../bits/search";
import { CustomTreeItem } from "../bits/treeMenu";
import { PublicationContext } from "../contexts/WithPublication/PublicationContext";
import { useQuery } from "../hooks/useQuery";
import { useUser } from "../hooks/useUser";
import type { ContentsItemWithSearchHit } from "../utils";
import {
  createSearchResultTree,
  extractSectionPath,
  scrollToElementOrAncestor,
} from "../utils";
import { searchSuggestionsTitleMapping } from "../utils/searchSuggestionsTitleMapping";

type AutocompleteOption = {
  title?: string;
  category?: string;
};

const filter = createFilterOptions<AutocompleteOption>();

export const PublicationSearch = ({ isOpen }: { isOpen: boolean }) => {
  const query = useQuery();
  const urlSearchQuery = query.get("search");
  const [searchInputValue, setSearchInputValue] = useState(
    urlSearchQuery ?? "",
  );
  const [searchQuery, setSearchQuery] = useState(urlSearchQuery ?? "");
  const { addNewRecentSearch, user } = useUser();
  const {
    publication,
    selectByHash,
    contents,
    currentSection,
    setScrollDisabled,
    setPendingHash,
    selectSection,
  } = useContext(PublicationContext);
  const [selectedItem, setSelectedItem] = useState<string>("");

  const { data, isLoading, isFetched } = trpc.searchPublication.useQuery(
    {
      id: publication.id,
      search: searchQuery,
    },
    {
      enabled: searchQuery.length > 2,
    },
  );

  const contentsWithSearchResults = useMemo(() => {
    return createSearchResultTree(data?.hits ?? [], contents);
  }, [data?.hits, contents]);

  const allItemIds = useMemo(() => {
    const itemIds: string[] = [];
    const registerItemId = (item: ContentsItemWithSearchHit) => {
      if (item.children?.length) {
        itemIds.push(item.id);
        item.children.forEach(registerItemId);
      }
    };

    contentsWithSearchResults.forEach(registerItemId);

    return itemIds;
  }, [contentsWithSearchResults]);

  const apiRef = useTreeViewApiRef();
  const slots = useMemo(() => ({ item: CustomTreeItem }), []);

  const { data: searchSuggestions, isLoading: isLoadingSearchSuggestions } =
    trpc.searchSuggestions.useQuery(
      {
        search: searchInputValue,
      },
      {
        enabled: searchInputValue.length > 0,
      },
    );

  const autocompleteOptions = useMemo<AutocompleteOption[]>(() => {
    if (searchInputValue.length > 0) {
      const searches = [
        ...new Set(
          searchSuggestions?.map((option) => ({
            title: option.searchTerm,
            category: searchSuggestionsTitleMapping[option.type],
          })) ?? [],
        ),
      ];
      return searches;
    } else {
      const searches = [
        ...new Set(
          user?.recentSearches
            .filter((option) => option.publication === publication.id)
            .map((option) => ({
              title: option.query.trim(),
              category: "Recent searches",
            })) ?? [],
        ),
      ];
      return searches;
    }
  }, [
    searchInputValue.length,
    searchSuggestions,
    user?.recentSearches,
    publication.id,
  ]);

  useEffect(() => {
    if (currentSection?.id && isOpen) {
      const sectionPath = extractSectionPath(currentSection.id);
      if (sectionPath && sectionPath !== selectedItem) {
        setSelectedItem(sectionPath);
      }
      scrollToElementOrAncestor(
        sectionPath ? `search-nav-${sectionPath}` : undefined,
      );
    }
  }, [currentSection?.id, data?.hits, selectedItem, isOpen]);

  // Invalidate the user when the searchQuery is fetched to get the latest data
  useEffect(() => {
    if (!isFetched) return;
    addNewRecentSearch({
      query: searchQuery,
      publication: publication?.id,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetched]);

  const selectAndHighlightItem = useCallback(
    (_: SyntheticEvent, itemId: string | null) => {
      if (itemId && apiRef.current) {
        const item = apiRef.current.getItem(itemId);
        setScrollDisabled(true);
        setPendingHash(undefined);
        selectSection(itemId, item);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiRef, selectSection, setScrollDisabled],
  );

  const updateQueryValue = (
    _: unknown,
    newValue: string | AutocompleteOption | null,
  ) =>
    setSearchQuery(
      typeof newValue === "string" ? newValue : (newValue?.title ?? ""),
    );

  const loading = isLoading && searchQuery.length > 2;

  // Don't render anything if the panel is closed, large number results
  // can be expensive to render
  if (!isOpen) {
    return null;
  }

  return (
    <>
      <SearchInputContainer
        css={css`
          position: sticky;
        `}
      >
        <Autocomplete
          id="search-publication"
          freeSolo
          options={autocompleteOptions ?? []}
          onChange={updateQueryValue}
          onInputChange={(e: unknown, newValue: string) => {
            if (typeof newValue === "string" && newValue.length > 100) {
              return;
            }
            return e && setSearchInputValue(newValue ?? "");
          }}
          loading={searchInputValue.length > 0 && isLoadingSearchSuggestions}
          filterOptions={(options, params) => {
            const filtered = filter(options, params);
            return filtered || [];
          }}
          groupBy={(option) => option.category ?? ""}
          value={{ title: searchQuery }}
          getOptionLabel={(option) =>
            typeof option === "string" ? option : (option.title ?? "")
          }
          inputValue={searchInputValue}
          renderInput={(params) => (
            <div ref={params.InputProps.ref}>
              <SearchInput
                css={css`
                  width: 100%;
                `}
                placeholder="Search this publication"
                type="text"
                {...params.inputProps}
              />
            </div>
          )}
        />

        <SearchInputLabel>
          {!loading && !searchQuery && (
            <>
              <img
                css={css`
                  padding-left: 4px;
                  height: 10px;
                `}
                src={InfoIcon}
                alt="info"
              />
              Try searching for topics, terms in the index, or case names
            </>
          )}
          {loading && searchQuery && <div>Fetching results...</div>}
          {data && !loading && searchQuery.length > 2 && (
            <div>
              Found {data.nbHits} results for "{searchQuery}"
            </div>
          )}
        </SearchInputLabel>
      </SearchInputContainer>
      <div
        css={({ colours }) => css`
          height: 100%;
          box-shadow:
            0px 0px 4px 2px ${colours.accent.light._20} inset,
            0px 0px 0px 0.5px rgba(255, 255, 255, 1) inset,
            0px -6px 12px -2px rgba(255, 255, 255, 1) inset,
            0px 0px 12px -6px rgba(211, 209, 199, 1);
          opacity: 1;
          background-color: ${colours.accent.light._10};
          overflow-y: auto;
          scrollbar-width: thin;
        `}
      >
        {/* TODO Search suggestions and recent searches to be added here */}
        {loading && searchQuery && <LoadingSpinner />}
        {data && !loading && searchQuery.length > 2 && (
          <div>
            <RichTreeView
              apiRef={apiRef}
              items={contentsWithSearchResults}
              onItemClick={selectAndHighlightItem}
              selectedItems={selectedItem}
              expandedItems={allItemIds}
              slots={slots}
              slotProps={{
                item: {
                  searchView: true,
                  goToSection: selectByHash,
                  searchTerm: searchQuery,
                  publicationId: publication.filename,
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } as any,
              }}
            />
          </div>
        )}
        {!loading && searchQuery && searchQuery.length <= 2 && (
          <div>Search for more than 2 characters to get results</div>
        )}
      </div>
    </>
  );
};
